import {Map, List, Set} from 'immutable';
import {createSelector} from 'reselect';
import {
  iconColors,
  isNames,
  isNamesOrNumbers,
  isNumbers
} from '~/data/accessors/icon';
import {isNone} from '~/data/accessors/color';
import * as IconSelectors from '~/selectors/IconSelectors';
import IconConstants from '~/constants/DesignIconConstants';
import {MINIMUM_DPI} from '~/constants/UploadConstants';
import {isText} from '~/data/accessors/icon';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {getIntersectingIcons} from '~/selectors/IconButtonsSelectors';
import {getIconPosition} from '~/utils/geometry';
import {COLOR_MODES} from '~/constants/UploadConstants';

import {uploadResTestEnabled} from '~/selectors/ConfigSelectors';

const sleeveViewIds = [VIEW_ID_MAP.leftSleeve, VIEW_ID_MAP.rightSleeve];

export const getProductColorRemovalThreshold = state =>
  getCanvas(state).get('productColorRemovalThreshold');
export const disabledCanvas = state => getCanvas(state).get('disabled');

// duplicated, but cant move yet since circular dependency
export const getDesigns = state => state.design.present;
export const getCanvas = state => state.canvas.present || state.canvas;

export const getClipboardIconIds = state =>
  getCanvas(state).get('clipboard').toArray();

export const getClipboardSource = state =>
  getCanvas(state).get('clipboardSource');

export const getCurrentCanvas = state => getCanvas(state).get('currentCanvas');

export const getCurrentDesignId = state =>
  getCanvas(state).get('currentDesignId');

export const getDisableArrowKeys = state =>
  getCanvas(state).get('disableArrowKeys');

export const getLastIconId = state => getCanvas(state).get('lastIconId');

export const getLastArtIconId = state => getCanvas(state).get('lastArtIconId');

export const getLastNamesIconId = state =>
  getCanvas(state).get('lastNamesIconId');

export const getLastNumbersIconId = state =>
  getCanvas(state).get('lastNumbersIconId');

export const getLastTextIconId = state =>
  getCanvas(state).get('lastTextIconId');

export const getLastUploadIconId = state =>
  getCanvas(state).get('lastUploadIconId');

export const getLastSavedCanvas = state =>
  getCanvas(state).get('lastSavedCanvas');

export const getRecentUploadIconIds = state =>
  getCanvas(state).get('recentUploadIconIds');

export const getSelectedIconIds = state =>
  getCanvas(state).get('selectedIconIds').toArray();

export const hasClipping = state =>
  !getCanvas(state).get('clipboard').isEmpty();

export const hasSelectedIcon = state =>
  !!getCanvas(state).get('selectedIconIds').size;

export const hasMultipleSelectedIcons = state =>
  getCanvas(state).get('selectedIconIds').size > 1;

export const isLabBlankLoad = state => getCanvas(state).get('labBlankLoad');

export const selectedViewId = state => getCanvas(state).get('selectedViewId');

export const selectedIconId = state =>
  getCanvas(state).get('selectedIconIds').first();

export const sleevePrintEnabled = state =>
  getCanvas(state).get('sleevePrintEnabled');

export const getState = createSelector(
  [getCanvas, getLastSavedCanvas],
  (canvas, lastSavedCanvas) => {
    return Map({canvas, lastSavedCanvas});
  }
);

export const getCurrentDesign = createSelector(
  [getDesigns, getCurrentDesignId],
  (designs, designId) => {
    const design = designs.get(designId);
    if (design && !design.get('fetching')) return design;
  }
);

export const getIcons = createSelector(
  [getCurrentDesign, IconSelectors.getIcons],
  (design, icons) => {
    return design
      ? design
          .get('iconIds')
          .map(iconId => icons.get(iconId))
          .filter(icon => !!icon)
      : List();
  }
);

export const getIconFromCurrentDesign = createSelector(
  [getIcons],
  icons => iconId => {
    return icons?.find(icon => icon?.id === iconId);
  }
);

export const getIconsArray = createSelector([getIcons], icons => {
  return icons.toArray();
});

export const getIconsArrayJS = createSelector([getIcons], icons => {
  return icons.toArray().map(icon => icon.toJS());
});

export const getIconById = createSelector(
  [IconSelectors.getIcons],
  icons => id => {
    if (!id) {
      return null;
    }
    return icons.get(id) ? icons.get(id).toJS() : null;
  }
);

export const getSelectedIconsArray = createSelector(
  [getIcons, getSelectedIconIds],
  (icons, selectedIconIds) => {
    return icons.filter(icon => selectedIconIds.includes(icon.id)).toArray();
  }
);

export const getSelectedIconTypes = createSelector(
  [getSelectedIconsArray],
  selectedIcons => selectedIcons.map(icon => icon.type)
);

export const getLastIcon = createSelector(
  [getLastIconId, IconSelectors.getIcons],
  (lastIconId, icons) => {
    const icon = icons.get(lastIconId);
    return icon && icon.toObject();
  }
);

export const getLastIconColor = createSelector([getLastIcon], icon =>
  icon && icon.color && !isNone(icon.color) ? icon.color : undefined
);

export const getLastTextIcon = createSelector(
  [getLastTextIconId, IconSelectors.getIcons],
  (iconId, icons) => {
    const icon = icons.get(iconId);
    return icon && icon.toObject();
  }
);

export const getLastTextHeight = createSelector(
  [getLastTextIcon],
  icon => icon && icon.fontHeight
);

export const getLastTextOutlineColor = createSelector(
  [getLastTextIcon],
  icon => icon && icon.outlineColor
);

export const getLastTextEffectSettings = createSelector(
  [getLastTextIcon],
  icon =>
    icon && {
      effectSetting: icon.effectSetting,
      effectType: icon.effectType,
      effectName: icon.effectName
    }
);

export const getSelectedViewIcons = createSelector(
  [selectedViewId, getIcons],
  (viewId, icons) => {
    return icons.filter(icon => icon.viewId === viewId);
  }
);

export const allIconsSelected = createSelector(
  [getSelectedIconIds, getSelectedViewIcons],
  (selectedIcons, viewIcons) => {
    return viewIcons.size !== 0 && selectedIcons.length === viewIcons.size;
  }
);

export const getLastTextIconPosition = createSelector(
  [getCurrentDesign, getSelectedViewIcons],
  (design, icons) => {
    if (icons.size) {
      icons = icons.toJSON();
      const textIconIds = icons
        .filter(icon => isText(icon.type))
        .map(icon => icon.id);
      if (textIconIds && textIconIds.length) {
        return design.get('iconIds').findIndex(id => textIconIds.includes(id));
      }
    }
    return -1;
  }
);

export const getPreviousOutlineSettings = createSelector(
  [getLastTextIcon],
  icon =>
    icon && icon.fontOutlineAllowed && icon.outlineWidth
      ? {
          outlineColor: icon.outlineColor,
          outlineWidth: icon.outlineWidth
        }
      : {}
);

export const getPreviousOutlineAndTextEffects = createSelector(
  [getLastTextEffectSettings, getPreviousOutlineSettings],
  (effectSettings, outlineSettings) => ({...outlineSettings, ...effectSettings})
);

export const getRecentUploadIcons = createSelector(
  [IconSelectors.getIcons, getRecentUploadIconIds],
  (icons, iconIds) =>
    iconIds
      .reverse()
      .map(iconId => icons.find(icon => icon.id === iconId))
      .filter(icon => !!icon)
      .toArray()
);

export const getUploadIcons = createSelector([getIconsArrayJS], icons =>
  icons.filter(i => i.type === IconConstants.TYPES.upload)
);

export const getSpotModeUploadIcons = createSelector([getUploadIcons], icons =>
  icons.filter(icon => icon.colorMode === COLOR_MODES.SPOT)
);

export const getColorsFromIcons = (icons = []) => {
  return icons.reduce((colors, icon) => {
    return [
      ...colors,
      // eslint-disable-next-line no-unsafe-optional-chaining
      ...icon.spotColors?.reduce((spotColors, color) => {
        const {name, hexString} =
          color?.selectedUser ||
          color?.user ||
          color?.palette ||
          // eslint-disable-next-line no-unsafe-optional-chaining
          color?.original;
        // don't allow Transparent as a useable Pantone Match
        if (hexString === '00000000') return spotColors;
        return [...spotColors, {name, hexString}];
      }, [])
    ];
  }, []);
};

export const getColorsByIconIds = createSelector(
  [getIcons],
  icons =>
    (iconIds = []) => {
      return getColorsFromIcons(
        icons.filter(icon => iconIds.includes(icon.id))
      );
    }
);

export const getUploadColors = createSelector(
  [getSpotModeUploadIcons],
  getColorsFromIcons
);

export const viewIcons = createSelector([getIcons], icons => {
  return icons.reduce((views, icon) => {
    const view = views.get(icon.viewId) || List();
    return views.set(icon.viewId, view.push(icon));
  }, Map());
});

export const getFirstSleeveView = createSelector(
  [viewIcons],
  canvasViewIcons => {
    const arrayOfViewIds = [...canvasViewIcons.keys()];
    return arrayOfViewIds.find(viewId => {
      return (
        viewId === VIEW_ID_MAP['leftSleeve'] ||
        viewId === VIEW_ID_MAP['rightSleeve']
      );
    });
  }
);

export const hasSleeveDesign = createSelector(
  [getFirstSleeveView],
  firstSleeve => {
    return !!firstSleeve;
  }
);

export const viewColors = createSelector([viewIcons], viewIcons => {
  return viewIcons.map(icons => {
    return icons
      .filter(icon => !isNamesOrNumbers(icon.type))
      .reduce((colors, icon) => colors.union(iconColors(icon)), Set());
  });
});

export const viewUploadedIcons = createSelector([viewIcons], viewIcons => {
  return viewIcons.filter(icons =>
    icons.some(icon => icon.type === IconConstants.TYPES.upload)
  );
});

export const viewColorsJs = createSelector([viewColors], viewColors =>
  viewColors.toJS()
);

export const viewColorCount = createSelector(
  [viewColors],
  viewColors => viewColors.size
);

export const colorsOnView = createSelector(
  [getSelectedViewIcons],
  viewIcons => {
    return viewIcons
      .filter(icon => !isNamesOrNumbers(icon.type))
      .reduce((colors, icon) => colors.union(iconColors(icon)), Set())
      .toArray();
  }
);

export const canvasColors = createSelector([viewColors], viewColors =>
  viewColors.reduce((acc, colors) => acc.union(colors), Set()).toArray()
);

export const canvasColorsCount = createSelector(
  [canvasColors],
  canvasColors => canvasColors?.length
);

function hasIconType(state, type) {
  const icons = getIcons(state);
  return icons.some(icon => icon.type === type);
}

export const hasArtIcon = state =>
  hasIconType(state, IconConstants.TYPES.clipart);

export const hasNamesNamesAndNumbers = state =>
  hasIconType(state, IconConstants.TYPES.names);

export const hasNumbersNamesAndNumbers = state =>
  hasIconType(state, IconConstants.TYPES.numbers);

export const hasText = state => hasIconType(state, IconConstants.TYPES.text);

export const hasUpload = state =>
  hasIconType(state, IconConstants.TYPES.upload);

export const namesIconSelector = createSelector(getIcons, icons =>
  icons.find(icon => isNames(icon.type))
);

export const numbersIconSelector = createSelector(getIcons, icons =>
  icons.find(icon => isNumbers(icon.type))
);

export const getNamesOrNumbersIcon = createSelector(
  [namesIconSelector, numbersIconSelector],
  (namesIcon, numbersIcon) => namesIcon || numbersIcon
);

export const hasNamesAndNumbers = createSelector(
  [hasNamesNamesAndNumbers, hasNumbersNamesAndNumbers],
  (hasNamesNamesAndNumbers, hasNumbersNamesAndNumbers) =>
    hasNamesNamesAndNumbers || hasNumbersNamesAndNumbers
);

export const isDirty = createSelector(
  [getCurrentCanvas, getLastSavedCanvas],
  (currentCanvas, lastSavedCanvas) => {
    return currentCanvas !== lastSavedCanvas;
  }
);

export const isBlank = createSelector([getIcons], icons => icons.size === 0);

export const getHighestColorSleeveViewId = createSelector(
  [viewColors],
  viewColors => {
    return viewColors
      .filter((colors, viewId) => sleeveViewIds.includes(viewId))
      .sort((colorsA, colorsB) => colorsB.size - colorsA.size)
      .map((colors, viewId) => viewId)
      .first();
  }
);

export const getHighestColorSleeveIcon = createSelector(
  [getHighestColorSleeveViewId, viewIcons],
  (viewId, viewIcons) =>
    viewId &&
    viewIcons.size &&
    viewIcons
      .get(viewId)
      .sort((iconA, iconB) => {
        const bColors = iconB.colors ? iconB.colors.size : 1;
        const aColors = iconA.colors ? iconA.colors.size : 1;
        return bColors - aColors;
      })
      .first()
);

export const selectedIcon = createSelector(
  [selectedIconId, getIcons],
  (iconId, icons) => {
    if (!iconId) return null;
    return icons.find(icon => icon.id === iconId);
  }
);

export const getSelectedIconType = createSelector(
  [selectedIcon],
  icon => icon && icon.type
);

export const getSelectedIconId = createSelector(
  [selectedIcon],
  icon => icon && icon.id
);

export const isSelectedIconRecolored = createSelector(
  [selectedIcon],
  icon => icon && icon.recolored
);

export const backwardable = createSelector(
  [getIcons, selectedIconId],
  (icons, iconId) => {
    const index = icons.findIndex(icon => icon.id === iconId);
    return index > 0;
  }
);

export const forwardable = createSelector(
  [getIcons, selectedIconId],
  (icons, iconId) => {
    const index = icons.findIndex(icon => icon.id === iconId);
    return index !== -1 && index < icons.size - 1;
  }
);

export const backwardableOnIntersection = createSelector(
  [getIntersectingIcons, getIcons, selectedIconId],
  (intersectingIcons, icons, iconId) => {
    const intersectingIconIds = getIntersectingIconsIds({
      intersectingIcons,
      icons,
      iconId
    });

    if (!intersectingIconIds) {
      return false;
    }

    const index = intersectingIconIds.findIndex(icon => icon?.id === iconId);

    return index > 0 && index <= intersectingIconIds.size - 1;
  }
);

export const forwardableOnIntersection = createSelector(
  [getIntersectingIcons, getIcons, selectedIconId],
  (intersectingIcons, icons, iconId) => {
    const intersectingIconIds = getIntersectingIconsIds({
      intersectingIcons,
      icons,
      iconId
    });

    if (!intersectingIconIds) {
      return false;
    }

    const index = intersectingIconIds.findIndex(icon => icon?.id === iconId);

    return index >= 0 && index <= intersectingIconIds.size - 2;
  }
);

const getIntersectingIconsIds = ({intersectingIcons, icons, iconId}) => {
  if (!intersectingIcons || !icons || !iconId) {
    return false;
  }

  const isSelectedIconIntersecting = intersectingIcons
    ? intersectingIcons[iconId]
    : false;

  if (!isSelectedIconIntersecting) {
    return false;
  }

  const intersectingIconIds = icons.filter(icon => {
    if (intersectingIcons[icon?.id] === true) {
      return icon;
    }
  });

  return intersectingIconIds;
};

export const isCenterAligned = createSelector(
  [getSelectedIconsArray],
  icons => {
    return icons.every(icon => {
      return icon.x === icons[0].x;
    });
  }
);

export const isMiddleAligned = createSelector(
  [getSelectedIconsArray],
  icons => {
    return icons.every(icon => {
      return icon.y === icons[0].y;
    });
  }
);

export const isLeftAligned = createSelector([getSelectedIconsArray], icons => {
  return icons.every(icon => {
    return getIconPosition(icon).left === getIconPosition(icons[0]).left;
  });
});

export const isRightAligned = createSelector([getSelectedIconsArray], icons => {
  return icons.every(icon => {
    return getIconPosition(icon).right === getIconPosition(icons[0]).right;
  });
});

export const isTopAligned = createSelector([getSelectedIconsArray], icons => {
  return icons.every(icon => {
    return getIconPosition(icon).top === getIconPosition(icons[0]).top;
  });
});

export const isBottomAligned = createSelector(
  [getSelectedIconsArray],
  icons => {
    return icons.every(icon => {
      return getIconPosition(icon).bottom === getIconPosition(icons[0]).bottom;
    });
  }
);

export const getLowResUploadId = createSelector([selectedIcon], icon => {
  if (!icon || !['photo', 'spot'].includes(icon.colorMode)) return false;
  const {width, height, originalImageWidth, originalImageHeight} = icon;

  const lowWidth = originalImageWidth / width < MINIMUM_DPI;
  const lowHeight = originalImageHeight / height < MINIMUM_DPI;
  return (lowWidth || lowHeight) && icon.id;
});

// used to a combined with getLowResUpload logic above, but needed to separate out the check from the warning.
// getLowResolutionWarning could probably be safely removed, but need a confirmation from product on removing old code
// from the failed low res test
export const getLowResolutionWarning = createSelector(
  [getLowResUploadId, uploadResTestEnabled],
  (isLowRes, testEnabled) => {
    const WARNING_MESSAGE =
      'This file may not print clearly. Try a different image or a version of this image with higher resolution.';

    return isLowRes && testEnabled ? WARNING_MESSAGE : null;
  }
);
